import axios from 'axios';
import type {
  ApiResponse,
  DashboardStats,
  User,
  Subscription,
  AppVersion,
  AppConfig,
  PaginatedResponse,
  NotificationPayload,
} from '../types';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/api/v1/admin`
    : 'http://localhost:3000/api/v1/admin',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('plus_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('plus_admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const loginAdmin = async (username: string, password: string) => {
  const { data } = await api.post<ApiResponse<{ accessToken: string }>>('/auth/login', {
    username,
    password,
  });
  return { token: data.data.accessToken };
};

// Dashboard
export const getDashboard = async () => {
  const { data } = await api.get<ApiResponse<DashboardStats>>('/dashboard');
  return data.data;
};

// Users
export const getUsers = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}) => {
  const { data } = await api.get<ApiResponse<PaginatedResponse<User>>>('/users', { params });
  return data.data;
};

export const getUser = async (id: string) => {
  const { data } = await api.get<ApiResponse<User>>(`/users/${id}`);
  return data.data;
};

export const extendTrial = async (id: string, days: number) => {
  const { data } = await api.put<ApiResponse<User>>(`/users/${id}/extend-trial`, { days });
  return data.data;
};

export const grantSubscription = async (id: string, days: number) => {
  const { data } = await api.post<ApiResponse<User>>(`/users/${id}/grant-subscription`, { days });
  return data.data;
};

export const toggleUserActive = async (id: string) => {
  const { data } = await api.put<ApiResponse<User>>(`/users/${id}/toggle-active`);
  return data.data;
};

// Subscriptions
export const getSubscriptions = async (params: {
  page?: number;
  limit?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
}) => {
  const { data } = await api.get<ApiResponse<PaginatedResponse<Subscription>>>('/subscriptions', {
    params,
  });
  return data.data;
};

export const exportSubscriptions = async (params: {
  startDate?: string;
  endDate?: string;
}) => {
  const response = await api.get('/subscriptions/export', {
    params,
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'subscriptions.csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
};

// Notifications
export const sendBroadcast = async (payload: NotificationPayload) => {
  const { data } = await api.post<ApiResponse<{ sent: number }>>('/notifications/broadcast', payload);
  return data.data;
};

// App Versions
export const getAppVersions = async () => {
  const { data } = await api.get<ApiResponse<{ versions: AppVersion[] }>>('/app-versions');
  return data.data.versions;
};

export const createAppVersion = async (version: Omit<AppVersion, 'id' | 'createdAt'>) => {
  const { data } = await api.post<ApiResponse<AppVersion>>('/app-versions', version);
  return data.data;
};

export const updateAppVersion = async (id: string, version: Partial<AppVersion>) => {
  const { data } = await api.put<ApiResponse<AppVersion>>(`/app-versions/${id}`, version);
  return data.data;
};

// App Config
export const getAppConfig = async () => {
  const { data } = await api.get<ApiResponse<{ configs: AppConfig[] }>>('/app-config');
  return data.data.configs;
};

export const updateAppConfig = async (key: string, value: string) => {
  const { data } = await api.put<ApiResponse<AppConfig>>(`/app-config/${key}`, { value });
  return data.data;
};

export default api;
